import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IUserRole } from '@techspert-io/auth';
import { ToastService } from '@techspert-io/user-alerts';
import { forkJoin, Subject, takeUntil, tap } from 'rxjs';
import { MaterialModule } from '../../../../shared/patterns/material-module';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-manage-user-roles',
  templateUrl: 'manage-user-roles.component.html',
  styleUrls: ['manage-user-roles.component.scss'],
  imports: [MaterialModule],
})
export class ManageUserRolesComponent implements OnInit, OnDestroy {
  @Input() userId: string;

  @Output() isSubmittingSignal = new EventEmitter<boolean>();

  isLoading = true;

  roles: IUserRole[];
  assignedRoles: Set<string>;

  form = new FormGroup({});

  private destroy$ = new Subject();
  private isSubmittingValue = false;

  get isSubmitting() {
    return this.isSubmittingValue;
  }

  set isSubmitting(isSubmitting: boolean) {
    this.isSubmittingValue = isSubmitting;

    if (isSubmitting) {
      this.form.disable();
    } else {
      this.setupRoleControls();
    }

    this.isSubmittingSignal.emit(isSubmitting);
  }

  constructor(
    private userService: UserService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    forkJoin({
      roles: this.userService.getAllRoles(),
      userRoleIds: this.userService.getRolesForUser(this.userId),
    })
      .pipe(
        tap(({ roles, userRoleIds }) => {
          this.roles = roles;
          this.assignedRoles = new Set(userRoleIds);
          this.setupRoleControls();
        }),
        tap(() => (this.isLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  submitForm() {
    const selectedRoles = this.getSelectedRoles();

    if (this.roleAssignmentIsUpdated([...this.assignedRoles], selectedRoles)) {
      this.toastService.sendMessage('No role changes detected', 'error');
      return;
    }

    this.isSubmitting = true;

    this.userService
      .manageUserRoles(this.userId, selectedRoles)
      .pipe(
        tap(() => (this.assignedRoles = new Set(selectedRoles))),
        tap(() => (this.isSubmitting = false))
      )
      .subscribe();
  }

  private setupRoleControls() {
    this.roles.forEach((role) => {
      this.setupRoleControl(role);
    });
  }

  private setupRoleControl(role: IUserRole) {
    if (!this.form.controls[role.id]) {
      this.form.addControl(role.id, new FormControl(null));
    }

    const control = this.form.get(role.id);

    control.setValue(this.assignedRoles.has(role.id));

    if (role.canAssign) {
      control.enable();
    } else {
      control.disable();
    }
  }

  private getSelectedRoles() {
    return Object.entries(this.form.getRawValue()).flatMap(
      ([roleId, isSelected]) => (!!isSelected ? [roleId] : [])
    );
  }

  private roleAssignmentIsUpdated(
    previousRoleIds: string[],
    currentRoleIds: string[]
  ) {
    return (
      JSON.stringify(previousRoleIds.sort()) ===
      JSON.stringify(currentRoleIds.sort())
    );
  }
}
